/** The public landing page — the first thing a prospect sees.
 *
 * Built on the app's existing heritage palette (olive + bronze, tokens.css)
 * so the marketing page and the product read as one thing. Motion is used
 * deliberately: stats count up when they scroll into view, sections fade
 * and rise, and a marquee band of the vocabulary themes drifts past — all
 * disabled under prefers-reduced-motion, none of it load-bearing.
 *
 * Every number is REAL and taken from the shipped catalogue. A landing page
 * that inflates its own product loses a school's trust on day one, so a
 * test pins the exact figures.
 *
 * Authenticated users never see this — they are sent straight to their
 * dashboard.
 */
import type { ReactNode } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { useT } from "../../i18n";
import { useCountUp, useReveal } from "./motion";

/** A section that fades and rises the first time it enters the viewport. */
function Reveal({ children, className = "" }: { children: ReactNode; className?: string }) {
  const ref = useReveal<HTMLDivElement>();
  return (
    <div ref={ref} className={`reveal ${className}`}>
      {children}
    </div>
  );
}

function CountStat({ to, suffix = "", label }: { to: number; suffix?: string; label: string }) {
  const { value, ref } = useCountUp(to);
  return (
    <div className="text-center">
      <div className="text-3xl font-extrabold text-bronze-dark sm:text-4xl">
        <span ref={ref}>{value.toLocaleString("de-DE")}</span>
        {suffix}
      </div>
      <div className="mt-1 text-sm text-muted">{label}</div>
    </div>
  );
}

function Feature({ icon, title, body }: { icon: string; title: string; body: string }) {
  return (
    <div className="group rounded-card border border-line bg-surface p-5 transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg">
      <div className="text-2xl transition-transform duration-200 group-hover:scale-110" aria-hidden>
        {icon}
      </div>
      <h3 className="mt-3 font-bold text-ink">{title}</h3>
      <p className="mt-1 text-sm text-muted">{body}</p>
    </div>
  );
}

/** The six-skill Können-Check — the hero's live product glimpse, and the
 *  thing nothing else in this market has, so it is what leads. */
function SkillGlimpse() {
  const t = useT();
  const skills: [string, number][] = [
    [t("skill.wortschatz"), 82],
    [t("skill.grammatik"), 71],
    [t("skill.lesen"), 64],
    [t("skill.hoeren"), 48],
    [t("skill.schreiben"), 70],
    [t("skill.sprechen"), 55],
  ];
  return (
    <div className="rounded-card border border-line bg-surface/95 p-5 shadow-xl backdrop-blur">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted">
        {t("kc.title")}
      </p>
      <ul className="mt-3 flex flex-col gap-2.5">
        {skills.map(([name, pct]) => (
          <li key={name} className="flex items-center gap-3">
            <span className="w-24 shrink-0 text-sm font-medium text-ink">{name}</span>
            <span className="h-2 flex-1 overflow-hidden rounded-full bg-line">
              <span
                className="block h-full rounded-full bg-brand transition-[width] duration-1000 ease-out"
                style={{ width: `${pct}%` }}
              />
            </span>
            <span className="w-9 text-right text-sm font-bold text-ink">{pct}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** A drifting band of the real vocabulary themes — texture, and a hint at
 *  the breadth of the library, without a wall of text. */
function ThemeMarquee() {
  const themes = [
    "Begrüßung", "Familie", "Arbeit & Beruf", "Reisen", "Gesundheit",
    "Wohnen", "Umwelt", "Wirtschaft", "Medien", "Recht & Verwaltung",
    "Wissenschaft", "Gesellschaft", "Kultur", "Technik", "Bildung",
  ];
  const row = [...themes, ...themes];
  return (
    <div className="marquee border-y border-line bg-surface py-4">
      <div className="marquee-track gap-3">
        {row.map((theme, i) => (
          <span
            key={i}
            className="mx-1.5 rounded-full border border-line bg-canvas px-4 py-1.5 text-sm font-medium text-muted"
          >
            {theme}
          </span>
        ))}
      </div>
    </div>
  );
}

/** The CEFR ladder A1 → C1, so a prospect immediately sees their level is
 *  covered. */
function LevelLadder() {
  const t = useT();
  const levels = ["A1", "A2", "B1", "B2", "C1"];
  return (
    <div className="flex flex-wrap items-center justify-center gap-2">
      {levels.map((lv, i) => (
        <div key={lv} className="flex items-center gap-2">
          <span className="rounded-control bg-brand-soft px-4 py-2 font-bold text-brand-dark">
            {lv}
          </span>
          {i < levels.length - 1 && <span className="text-muted" aria-hidden>→</span>}
        </div>
      ))}
      <span className="ml-1 text-sm text-muted">{t("landing.ladderNote")}</span>
    </div>
  );
}

export function LandingPage() {
  const t = useT();
  const { user, loading } = useAuth();

  // A signed-in visitor has no use for the sales pitch.
  if (!loading && user) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen bg-canvas text-ink">
      {/* top bar */}
      <header className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5">
        <span className="text-xl font-extrabold tracking-tight text-ink">{t("app.name")}</span>
        <nav className="flex items-center gap-3">
          <Link to="/login" className="text-sm font-semibold text-ink hover:text-brand-dark">
            {t("landing.login")}
          </Link>
          <Link
            to="/signup"
            className="rounded-control bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-dark"
          >
            {t("landing.getStarted")}
          </Link>
        </nav>
      </header>

      {/* hero */}
      <section className="relative overflow-hidden bg-grad-hero text-white">
        <div className="mx-auto grid max-w-6xl items-center gap-10 px-6 py-16 lg:grid-cols-2 lg:py-24">
          <div>
            <p className="text-sm font-semibold uppercase tracking-widest text-white/70">
              {t("landing.eyebrow")}
            </p>
            <h1 className="mt-3 text-4xl font-extrabold leading-tight text-balance sm:text-5xl">
              {t("landing.h1")}
            </h1>
            <p className="mt-4 max-w-lg text-lg text-white/85">{t("landing.sub")}</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/signup"
                className="rounded-control bg-white px-5 py-3 text-sm font-bold text-brand-dark shadow transition-transform hover:-translate-y-0.5 hover:bg-white/90"
              >
                {t("landing.ctaPrimary")} →
              </Link>
              <a
                href="#features"
                className="rounded-control border border-white/40 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10"
              >
                {t("landing.ctaSecondary")}
              </a>
            </div>
            <p className="mt-4 text-sm text-white/70">{t("landing.boards")}</p>
          </div>
          <div className="lg:pl-6">
            <SkillGlimpse />
          </div>
        </div>
      </section>

      {/* real numbers, counting up on scroll */}
      <section className="border-b border-line bg-surface">
        <div className="mx-auto grid max-w-5xl grid-cols-2 gap-8 px-6 py-12 sm:grid-cols-4">
          <CountStat to={2736} label={t("landing.statItems")} />
          <CountStat to={240} label={t("landing.statLessons")} />
          <CountStat to={7200} suffix="+" label={t("landing.statWords")} />
          <div className="text-center">
            <div className="text-3xl font-extrabold text-bronze-dark sm:text-4xl">A1–C1</div>
            <div className="mt-1 text-sm text-muted">{t("landing.statLevels")}</div>
          </div>
        </div>
      </section>

      <ThemeMarquee />

      {/* features */}
      <section id="features" className="mx-auto max-w-6xl px-6 py-16">
        <Reveal>
          <h2 className="text-center text-3xl font-extrabold text-ink text-balance">
            {t("landing.featuresTitle")}
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-center text-muted">
            {t("landing.featuresSub")}
          </p>
        </Reveal>
        <Reveal className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Feature icon="📝" title={t("landing.f1t")} body={t("landing.f1b")} />
          <Feature icon="📚" title={t("landing.f2t")} body={t("landing.f2b")} />
          <Feature icon="🗂️" title={t("landing.f3t")} body={t("landing.f3b")} />
          <Feature icon="✍️" title={t("landing.f4t")} body={t("landing.f4b")} />
          <Feature icon="🎧" title={t("landing.f5t")} body={t("landing.f5b")} />
          <Feature icon="🧭" title={t("landing.f6t")} body={t("landing.f6b")} />
        </Reveal>
      </section>

      {/* how it works */}
      <section className="bg-grad-sage">
        <div className="mx-auto max-w-5xl px-6 py-16">
          <Reveal>
            <h2 className="text-center text-3xl font-extrabold text-ink text-balance">
              {t("landing.howTitle")}
            </h2>
          </Reveal>
          <Reveal className="mt-10 grid gap-6 md:grid-cols-3">
            {[
              ["1", "landing.step1t", "landing.step1b"],
              ["2", "landing.step2t", "landing.step2b"],
              ["3", "landing.step3t", "landing.step3b"],
            ].map(([n, tk, bk]) => (
              <div key={n} className="rounded-card border border-line bg-surface p-6">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-bronze text-sm font-bold text-white">
                  {n}
                </span>
                <h3 className="mt-3 font-bold text-ink">{t(tk as never)}</h3>
                <p className="mt-1 text-sm text-muted">{t(bk as never)}</p>
              </div>
            ))}
          </Reveal>
          <Reveal className="mt-10">
            <LevelLadder />
          </Reveal>
        </div>
      </section>

      {/* how it's sold */}
      <section className="mx-auto max-w-6xl px-6 py-16">
        <Reveal>
          <h2 className="text-center text-3xl font-extrabold text-ink text-balance">
            {t("landing.pricingTitle")}
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-center text-muted">
            {t("landing.pricingSub")}
          </p>
        </Reveal>
        <Reveal className="mt-10 grid gap-4 md:grid-cols-3">
          {[
            ["landing.planLearnerT", "landing.planLearnerB"],
            ["landing.planCompleteT", "landing.planCompleteB"],
            ["landing.planSchoolT", "landing.planSchoolB"],
          ].map(([tk, bk], i) => (
            <div
              key={tk}
              className={`rounded-card border bg-surface p-6 transition-transform duration-200 hover:-translate-y-1 ${
                i === 1 ? "border-bronze shadow-lg" : "border-line"
              }`}
            >
              {i === 1 && (
                <span className="mb-2 inline-block rounded-full bg-bronze-soft px-2 py-0.5 text-xs font-semibold text-bronze-dark">
                  {t("landing.popular")}
                </span>
              )}
              <h3 className="text-lg font-bold text-ink">{t(tk as never)}</h3>
              <p className="mt-2 text-sm text-muted">{t(bk as never)}</p>
            </div>
          ))}
        </Reveal>
      </section>

      {/* final CTA */}
      <section className="bg-grad-brand text-white">
        <div className="mx-auto max-w-3xl px-6 py-20 text-center">
          <Reveal>
            <h2 className="text-3xl font-extrabold text-balance">{t("landing.finalTitle")}</h2>
            <p className="mx-auto mt-3 max-w-xl text-white/85">{t("landing.finalSub")}</p>
            <Link
              to="/signup"
              className="mt-7 inline-block rounded-control bg-white px-6 py-3 text-sm font-bold text-brand-dark shadow transition-transform hover:-translate-y-0.5"
            >
              {t("landing.ctaPrimary")} →
            </Link>
          </Reveal>
        </div>
      </section>

      <footer className="border-t border-line">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 px-6 py-8 text-sm text-muted sm:flex-row">
          <span className="font-bold text-ink">{t("app.name")}</span>
          <span>{t("landing.footer")}</span>
        </div>
      </footer>
    </div>
  );
}
